import styled from '@emotion/styled';

import Box from '~/common/components/Box';
import Text from '~/common/components/Texts/Text';
import { formatCurrency } from '~/common/util/format';
import type { HistoricalCalculation } from '~/pages/IncomeCalculator/CalculationHistory/types';
import { REQUEST_REFERENCE_TYPE } from '~/pages/IncomeCalculator/enums';
import lang from '~/pages/IncomeCalculator/language.json';

const StyledRightBox = styled(Box)`
  display: flex;
  align-items: flex-end;
  justify-content: center;
  flex-direction: column;
`;
const StyledRentalLabels = styled(Text)`
  font-size: 0.75rem;
  color: ${(props) => props.theme.colors.gray[500]};
  font-weight: 400;
`;

type CalculationSummaryProps = {
  summary: HistoricalCalculation;
};

const CalculationSummary = ({ summary }: CalculationSummaryProps) => {
  const isEmploymentIncome =
    summary.requestReferenceType === REQUEST_REFERENCE_TYPE.employmentIncome;
  const isRentalIncome =
    summary.requestReferenceType === REQUEST_REFERENCE_TYPE.rentalIncome;

  return (
    <Box display="flex" justifyContent="space-between" width="100%">
      <Box lineHeight="1.7rem">
        <Text fontSize="md" fontWeight={600}>
          {summary.calculationDate} {summary.calculationTime}
        </Text>
        <Text fontSize="md" color="gray.600" fontWeight={400}>
          {summary.changedByUserName}
        </Text>
        <Text fontSize="md" color="gray.600">
          {summary.calculatorTypeName}
        </Text>
      </Box>
      {isEmploymentIncome && (
        <StyledRightBox>
          <Text fontSize="2xl" fontWeight={600} textAlign="center">
            {formatCurrency(summary.calculatedMonthlyAmount)}
          </Text>
          {summary.isOverride && (
            <Text fontSize="md" fontWeight={500} pt={1}>
              {lang.CALC_HISTORY_OVERRIDE}
            </Text>
          )}
        </StyledRightBox>
      )}
      {isRentalIncome && (
        <Box display="flex" pt={2} gap={4}>
          <Box display="flex" flexDirection="column" alignItems="flex-end">
            <Text fontSize="2xl" fontWeight={600}>
              {formatCurrency(summary.rentalIncomePitia)}
            </Text>
            <StyledRentalLabels>{lang.CALC_HISTORY_PITIA}</StyledRentalLabels>
          </Box>
          <Box display="flex" flexDirection="column" alignItems="flex-end">
            <Text fontSize="2xl" fontWeight={400}>
              {formatCurrency(summary.calculatedMonthlyAmount)}
            </Text>
            <StyledRentalLabels>
              {lang.CALC_HISTORY_NET_RENTAL_INCOME}
            </StyledRentalLabels>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default CalculationSummary;
